import { setCookie } from '@cian/utils/lib/browser/cookie';
import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { trackDealTypeClick, trackSavedSearchClick } from './tracking';
import { fetchFavoriteEntities } from '../../actions/favoriteEntities';
import { setFavoriteEntityType } from '../../actions/filters';
import { EntityTypeToggle } from '../../components/EntityTypeToggle';
import { UXFEEDBACK_COOKIE_KEY } from '../../constants/uxfeedback';
import { useFavoritesUxfeedbackCampaign } from '../../hooks/uxfeedback';
import { selectResults, selectUser } from '../../selectors';
import { EEntityType, ETabType } from '../../types/filters';
import { EGetFavoriteEntitiesStatus } from '../../types/results';
import { useApplicationContext } from '../../utils/applicationContext';
import { getSavedSearches } from '../../thunk/savedSearches/getSavedSearches';
import { addQueryToHistory } from '../../hooks/browserHistory';
import { selectActiveTab } from '../../selectors/activeTab';
import { initializeActiveTabFromUrl, setActiveTab } from '../../actions/activeTab';
import { isAbUseExperimentGroup, selectExperiments } from '../../selectors/experiments';
import { PERS_SAVED_SEARCH_FAVORITES_MERGE_EXPERIMENT } from '../../constants/experiments';

export const EntityTypeToggleContainer = () => {
  const dispatch = useDispatch();
  const { config } = useApplicationContext();
  const { status } = useSelector(selectResults);
  const user = useSelector(selectUser);
  const { activeTab, isInitialized } = useSelector(selectActiveTab);
  const { sendFavoritesUxfeedbackCampaign } = useFavoritesUxfeedbackCampaign();
  const uxfeedbackCookieKey = config.get<string>('uxfeedback.favorites.cookieKey') || UXFEEDBACK_COOKIE_KEY;
  const experiments = useSelector(selectExperiments);
  const isExperimentEnabled = isAbUseExperimentGroup(experiments, PERS_SAVED_SEARCH_FAVORITES_MERGE_EXPERIMENT, 'B');

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabFromURL = params.get('activeTab') as ETabType | null;

    if (isExperimentEnabled && tabFromURL) {
      dispatch(initializeActiveTabFromUrl({ activeTab: tabFromURL }));
    } else {
      const defaultTab = ETabType.Аdvertisements;
      dispatch(initializeActiveTabFromUrl({ activeTab: defaultTab }));
      addQueryToHistory({ activeTab: defaultTab });
    }

    // Слушаем изменения URL (кнопки назад/вперед)
    const handlePopState = () => {
      const newParams = new URLSearchParams(window.location.search);
      const newTab = newParams.get('activeTab') as ETabType | null;
      dispatch(initializeActiveTabFromUrl({ activeTab: newTab }));
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch]);

  const setEntityType = (entityType: EEntityType | null) => {
    trackDealTypeClick(entityType);
    dispatch(setFavoriteEntityType(entityType));
    dispatch(fetchFavoriteEntities());
    sendFavoritesUxfeedbackCampaign();
  };

  //обработчик клика по табу "Объявления"
  const handleAdvertisementsClick = React.useCallback(() => {
    addQueryToHistory({ activeTab: ETabType.Аdvertisements });
    dispatch(setActiveTab({ activeTab: ETabType.Аdvertisements, isInitialized: true }));
    setEntityType(null);
  }, [setEntityType]);

  //обработчик клика по табу "ЖК"
  const handleJKClick = React.useCallback(() => {
    addQueryToHistory({ activeTab: ETabType.JK });
    dispatch(setActiveTab({ activeTab: ETabType.JK, isInitialized: true }));
    setEntityType(EEntityType.Newbuilding);
  }, [setEntityType]);

  //обработчик клика по табу "Поиски"
  const handleSavedSearchClick = React.useCallback(() => {
    addQueryToHistory({ activeTab: ETabType.SavedSearches });
    dispatch(setActiveTab({ activeTab: ETabType.SavedSearches, isInitialized: true }));

    trackSavedSearchClick();
    setCookie(uxfeedbackCookieKey, 'true', { domain: '.cian.ru', path: '/' });

    dispatch(getSavedSearches());
  }, [uxfeedbackCookieKey]);

  const clickSavedSearch = React.useCallback(() => {
    trackSavedSearchClick();
    setCookie(uxfeedbackCookieKey, 'true', { domain: '.cian.ru', path: '/' });

    window.location.href = 'https://my.cian.ru/saved-search/';
  }, [uxfeedbackCookieKey]);

  /* istanbul ignore next */
  const showSaveSearchEntity = user.isAuthenticated && !user.isAgent;

  if (!isInitialized || status === EGetFavoriteEntitiesStatus.Failed) {
    return null;
  }

  return (
    <EntityTypeToggle
      type={activeTab}
      showSaveSearchEntity={showSaveSearchEntity}
      clickSavedSearch={clickSavedSearch}
      onAdvertisementsClick={handleAdvertisementsClick}
      onJKClick={handleJKClick}
      onSavedSearchClick={handleSavedSearchClick}
    />
  );
};
